import { Typography, Box, Button } from "@mui/material"
import { DataGrid, GridRowModes, GridRowModesModel, GridRowModel } from "@mui/x-data-grid"
import AddIcon from "@mui/icons-material/Add"
import { useState } from "react"
import getInvoiceListColumns from "./getInvoiceListColumns"
import {
  useGetInvoicesQuery,
  useUpdateInvoiceMutation,
  useDeleteInvoiceMutation,
  UpdateInvoiceParams
} from "src/api/modules/invoice-api"
import { toast } from "react-toastify"
import { PageLoader } from "src/shared/components"
import { useNavigate } from "react-router-dom"
import ROUTE_PATHS from "src/routes/route-path"

const InvoiceList = () => {
  const navigate = useNavigate()
  const { data: invoices = [], isLoading } = useGetInvoicesQuery({ page: 1, limit: 10 })
  const [updateInvoice, { isLoading: isUpdating }] = useUpdateInvoiceMutation()
  const [deleteInvoice, { isLoading: isDeleting }] = useDeleteInvoiceMutation()
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({})

  const handleEdit = (id: string) => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } })
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteInvoice({ id })
      toast.success("Invoice deleted successfully")
    } catch (error) {
      console.error("Error deleting invoice:", error)
      toast.error("Failed to delete invoice")
    }
  }

  const handleSave = (id: string) => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } })
  }

  const handleCancel = (id: string) => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true }
    })
  }

  const handleProcessRowUpdate = async (newRow: GridRowModel) => {
    try {
      await updateInvoice(newRow as UpdateInvoiceParams)
      return newRow
    } catch (error) {
      console.error("Error updating invoice:", error)
      throw error
    }
  }

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel)
  }

  const isInEditMode = (id: string) => rowModesModel[id]?.mode === GridRowModes.Edit

  const invoiceColumns = getInvoiceListColumns(handleEdit, handleDelete, handleSave, handleCancel, isInEditMode)

  return (
    <Box
      sx={{
        height: "100vh",
        width: "100%",
        p: 2,
        display: "flex",
        flexDirection: "column"
      }}
    >
      <PageLoader isLoading={isUpdating || isDeleting} />
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h5" component="h2">
          Invoice Management
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate(ROUTE_PATHS.CREATE_INVOICE)}>
          Add Invoice
        </Button>
      </Box>
      <Box sx={{ flexGrow: 1 }}>
        <DataGrid
          loading={isLoading}
          density="compact"
          rows={invoices as any}
          showColumnVerticalBorder
          columns={invoiceColumns as any}
          editMode="row"
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          processRowUpdate={handleProcessRowUpdate}
          onProcessRowUpdateError={error => console.error("Row update error:", error)}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 }
            }
          }}
          disableRowSelectionOnClick
          sx={{
            height: "100%",
            border: theme => `1px solid ${theme.palette.divider}`,
            "& .MuiDataGrid-cell": {
              borderTop: theme => `1px solid ${theme.palette.divider}`
            },
            "& .MuiDataGrid-cell:focus": {
              outline: "none"
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "action.hover"
            }
          }}
        />
      </Box>
    </Box>
  )
}

export default InvoiceList
